import { css } from "vite-css-in-js"
import { computed, provide, shallowRef, watch } from "vue"
import { ShadowContainer, defineComponent, defineGlobals, inferProps, type ReactiveComponent } from "vue-utils"
import IncidentFormSection from "./IncidentFormSection"
import { IncidentLogHandlerInjectionKey, type IncidentLogHandler } from "./IncidentLogHandler"
import QuickNavigation from "./QuickNavigation"
import { useIncidentLogFormScrolling } from "./formScrolling"
import { getIncidentFormButtonSections, getIncidentFormSections } from "./sections"

export interface Props {
	handler: IncidentLogHandler
	setupForm?(form: HTMLFormElement): void
}

const containerStyles = css`
	display: flex;
	gap: 0.5rem;
	height: 100%;

	& > div:nth-child(1) {
		display: flex;
		flex-direction: column;
		flex-grow: 1;
		height: 100%;
	}

	@media print {
		display: contents;

		& > div:nth-child(1) {
			display: contents;
		}

		& > div:nth-child(2) {
			display: none;
		}
	}

	@media (max-width: 96rem) {
		& > div:nth-child(2) {
			display: none;
		}
	}
`

const formStyles = css`
	display: flex;
	flex-direction: column;
	flex: 1 1 0;
	gap: 1rem;
	height: 100%;

	& > div {
		padding: 0;
	}

	& > div:nth-child(1) {
		overflow: auto;
	}

	@media print {
		display: contents;
		height: 100%;
		flex-basis: auto;
		overflow: initial;

		& > section {
			display: block;
			break-inside: avoid;

			+ section {
				margin-bottom: 1rem;
			}
		}
	}
`

const buttonSectionStyles = css`
	section {
		padding-bottom: 1rem;

		&:last-child {
			padding-bottom: 1.25rem;
		}
	}
`

const IncidentForm: ReactiveComponent<Props> = (props) => {
	const formContainer = shallowRef<HTMLFormElement>()
	const scrollContainer = shallowRef<HTMLDivElement>()
	const sections = computed(() => getIncidentFormSections(props.handler))
	const buttonSections = computed(() => getIncidentFormButtonSections(props.handler))

	const { section, setSection } = useIncidentLogFormScrolling({
		contentRef: scrollContainer,
		sections,
	})

	watch(formContainer, (formEl) => formEl && props.setupForm?.(formEl))
	provide(IncidentLogHandlerInjectionKey, props.handler)

	defineGlobals({ incident: props.handler.incident, handler: props.handler })

	return () => (
		<div class={containerStyles}>
			<div>
				<form class={formStyles} onSubmit={(e) => e.preventDefault()} ref={formContainer} novalidate>
					<div class="vu-shadow-container" ref={scrollContainer}>
						{sections.value.map((section) => (
							<IncidentFormSection key={section.id} section={section} />
						))}
					</div>
					{buttonSections.value.length > 0 && (
						<ShadowContainer class={buttonSectionStyles}>
							{buttonSections.value.map((section) => (
								<IncidentFormSection key={section.id} section={section} />
							))}
						</ShadowContainer>
					)}
				</form>
			</div>
			<QuickNavigation sections={sections.value} section={section.value} setSection={setSection} />
		</div>
	)
}

export default defineComponent(IncidentForm, inferProps<Props>())
